import React from "react";

import { IconDashboard } from "~/components/icon";
import type { NodeId } from "~/models/dto";
import type { MetricQuery } from "~/models/metric-query";
import type { Time } from "~/tools/date-time/time";
import type { Loading, Success } from "~/tools/remote-data";
import { Template } from "~/tools/template";

import { normalizePinnedMetricComponent } from "../../bottom_content/view/panes/east/pinned-items/PinnedItem";
import { DashboardChartPluginSpec, DashboardWidgetSpec } from "../../common/dashboard-spec";
import { TimeSeriesQuery } from "../../common/dashboard-spec/_time-series-query";
import { generatePath } from "../../generic/routing";
import { queryFullComponent } from "../../queries/components";
import { useQuery } from "../../queries/react-query";
import { COMPONENT_PATH } from "../../routes/Routes";
import { useExplorationDrawerState } from "../react/drawer/use-exploration-drawer-state";
import { fromMetricQuery as widgetQueryFromMetricQuery } from "../widget-form/query/spec";
import type { Spec as ChartSpec } from "../widget-form/spec";

interface Model {
	title: undefined | string;
	componentIdOrIdentifier: undefined | string | number | NodeId;
	timestamp: undefined | Time;
	metricQueries: ReadonlyArray<MetricQuery>;
	chart: ChartSpec;
}

export interface AddToDashboardButtonProps {
	model: Loading | Success<Model>;
}

export const AddToDashboardButton: React.FC<AddToDashboardButtonProps> = ({ model }) => {
	const explorationDrawer = useExplorationDrawerState();

	const queryParams = model.getOrElse(undefined);

	const { data: component } = useQuery({
		enabled: queryParams?.componentIdOrIdentifier != null,
		refetchOnMount: false,
		refetchOnReconnect: false,
		refetchOnWindowFocus: false,
		select: normalizePinnedMetricComponent,

		...queryFullComponent({
			componentIdOrIdentifier: queryParams?.componentIdOrIdentifier ?? "",
			timestamp: queryParams?.timestamp,
		}),
	});

	return (
		<button
			className="button button-tertiary disabled:skeleton gap-2"
			disabled={!model.isSuccess()}
			onClick={() => {
				if (model.isSuccess()) {
					const widget = DashboardWidgetSpec.of({
						name: Template(model.value.title ?? ""),
						plugin: {
							links: component
								? [
										{
											url: Template(`/#${generatePath(COMPONENT_PATH, { identifier: component.identifier })}`),
											name: Template(`${component.name} (${component.type})`),
											targetBlank: false,
										},
									]
								: [],

							queries: model.value.metricQueries.map((query) =>
								TimeSeriesQuery.fromQuerySpec(widgetQueryFromMetricQuery(query)),
							),
							...DashboardChartPluginSpec.fromChartSpec(model.value.chart),
						},
					});

					explorationDrawer.openAddToDashboard([widget]);
				}
			}}
			type="button"
		>
			<IconDashboard className="text-15" />
			Add to dashboard
		</button>
	);
};
